import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Search, Plus, Minus, Trash2, ShoppingCart, Receipt, 
  DollarSign, CreditCard, QrCode, X, Printer, Check,
  Loader2, WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { queueOfflineSale, generateOfflineReceiptNumber, getCachedProducts, getCachedCategories } from '../lib/offlineStore';
import { useNetworkStatus } from '../lib/useNetworkStatus';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount);
};

const POSScreen = () => {
  const { api, hasFeature, organization, currentBranch, user } = useAuth();
  const { isOnline } = useNetworkStatus();
  const { 
    items, subtotal, discountTotal, setDiscountTotal, vatAmount, grandTotal, vatRate,
    addItem, incrementQuantity, decrementQuantity, removeItem, clearCart, itemCount 
  } = useCart();
  
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [tenderedAmount, setTenderedAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [discountType, setDiscountType] = useState('regular');
  const [discountCustomerName, setDiscountCustomerName] = useState('');
  const [discountCustomerId, setDiscountCustomerId] = useState('');
  const [discountRemarks, setDiscountRemarks] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isOfflineSale, setIsOfflineSale] = useState(false);

  // Load categories and products (with offline fallback)
  useEffect(() => {
    const loadData = async () => {
      try {
        if (navigator.onLine) {
          const [catsRes, prodsRes] = await Promise.all([
            api.get('/tenant/categories'),
            api.get('/tenant/products')
          ]);
          setCategories(catsRes.data);
          setProducts(prodsRes.data);
          if (catsRes.data.length > 0) {
            setSelectedCategory(catsRes.data[0].id);
          }
        } else {
          // Load from cache when offline
          const [cachedCats, cachedProds] = await Promise.all([
            getCachedCategories(),
            getCachedProducts()
          ]);
          if (cachedCats.length > 0 || cachedProds.length > 0) {
            setCategories(cachedCats);
            setProducts(cachedProds);
            if (cachedCats.length > 0) {
              setSelectedCategory(cachedCats[0].id);
            }
            toast.info('Loaded cached products (offline mode)');
          } else {
            toast.warning('No cached data - connect once to download products');
          }
        }
      } catch (error) {
        // Try cache on network error
        try {
          const [cachedCats, cachedProds] = await Promise.all([
            getCachedCategories(),
            getCachedProducts()
          ]);
          if (cachedCats.length > 0 || cachedProds.length > 0) {
            setCategories(cachedCats);
            setProducts(cachedProds);
            if (cachedCats.length > 0) {
              setSelectedCategory(cachedCats[0].id);
            }
            toast.info('Using cached products');
          } else {
            toast.error('Failed to load products');
          }
        } catch (cacheError) {
          toast.error('Failed to load products');
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [api]);

  // Barcode scanner handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if typing in an input
      if (e.target.tagName === 'INPUT') return;
      
      if (e.key === 'Enter' && barcodeInput) {
        handleBarcodeSearch(barcodeInput);
        setBarcodeInput('');
      } else if (e.key.length === 1) {
        setBarcodeInput(prev => prev + e.key);
        // Clear after 500ms of no input
        setTimeout(() => setBarcodeInput(''), 500);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
  
  const receiptWatermark = (() => {
    if (!lastSale) return '';
    if (lastSale?.reprint?.watermark) return lastSale.reprint.watermark;
    if (lastSale?.watermark) return lastSale.watermark;
    if (lastSale?.status === 'void') return 'VOID';
    if (lastSale?.status === 'refund') return 'REFUND';
    if (lastSale?.is_offline_sale || lastSale?.offline_receipt_number) return 'OFFLINE';
    return '';
  })();

  const isOfflineReceipt = Boolean(lastSale?.is_offline_sale || lastSale?.offline_receipt_number);

  return () => window.removeEventListener('keydown', handleKeyDown);
  }, [barcodeInput]);

  const handleBarcodeSearch = async (barcode) => {
    try {
      const response = await api.get(`/tenant/products/barcode/${barcode}`);
      addItem(response.data);
      toast.success(`Added ${response.data.name}`);
    } catch (error) {
      toast.error('Product not found');
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesCategory = !selectedCategory || p.category_id === selectedCategory;
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.barcode && p.barcode.includes(searchQuery));
    return matchesCategory && matchesSearch;
  });

  // Handle checkout (online or offline)
  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    const tendered = parseFloat(tenderedAmount) || 0;
    if (paymentMethod === 'cash' && tendered < grandTotal) {
      toast.error('Insufficient amount');
      return;
    }

    if (paymentMethod === 'qr_manual' && !paymentReference) {
      toast.error('Please enter payment reference');
      return;
    }

    if (['senior', 'pwd'].includes(discountType)) {
      if (!discountCustomerName.trim()) {
        toast.error('SC/PWD customer name is required');
        return;
      }
      if (!discountCustomerId.trim()) {
        toast.error('SC/PWD ID number is required');
        return;
      }
    }

    setProcessing(true);
    
    // OFFLINE checkout
    if (!navigator.onLine) {
      try {
        const branchCode = currentBranch?.code || 'OFF';
        const offlineReceiptNumber = generateOfflineReceiptNumber(branchCode);
        const changeAmount = Math.max(0, tendered - grandTotal);
        
        const offlineResult = await queueOfflineSale({
          tenantId: organization?.id,
          branchId: currentBranch?.id,
          cashierId: user?.id,
          offlineReceiptNumber,
          items: items,
          paymentMethod,
          paymentReference: paymentMethod === 'qr_manual' ? paymentReference : null,
          tenderedAmount: tendered,
          discountTotal,
          discountType,
          discountCustomerName,
          discountCustomerId,
          discountRemarks,
          subtotal,
          vatAmount,
          grandTotal,
          changeAmount
        });
        
        // Create offline sale response for receipt
        setLastSale({
          id: offlineResult.localId,
          receipt_number: offlineReceiptNumber,
          subtotal,
          discount_total: discountTotal,
          discount_type: discountType,
          discount_customer_name: discountCustomerName,
          discount_customer_id: discountCustomerId,
          discount_remarks: discountRemarks,
          vat_amount: vatAmount,
          grand_total: grandTotal,
          tendered_amount: tendered,
          change_amount: changeAmount,
          payment_method: paymentMethod,
          payment_reference: paymentReference,
          items: items.map(i => ({
            product_name: i.product_name,
            unit_price: i.unit_price,
            quantity: i.quantity,
            line_total: i.unit_price * i.quantity - i.line_discount
          })),
          created_at: new Date().toISOString(),
          cashier_name: user?.name,
          is_offline: true
        });
        
        setIsOfflineSale(true);
        clearCart();
        setShowCheckout(false);
        setShowReceipt(true);
        setTenderedAmount('');
        setPaymentReference('');
      setDiscountType('regular');
      setDiscountCustomerName('');
      setDiscountCustomerId('');
      setDiscountRemarks('');
        setDiscountType('regular');
        setDiscountCustomerName('');
        setDiscountCustomerId('');
        setDiscountRemarks('');
        toast.success('Sale saved offline - will sync when connected');
        
      } catch (error) {
        console.error('Offline checkout error:', error);
        toast.error('Failed to save offline sale');
      } finally {
        setProcessing(false);
      }
      return;
    }

    // ONLINE checkout
    try {
      const response = await api.post('/pos/sales', {
        items: items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          unit_price: i.unit_price,
          quantity: i.quantity,
          line_discount: i.line_discount,
          notes: i.notes,
        })),
        payment_method: paymentMethod,
        payment_reference: paymentMethod === 'qr_manual' ? paymentReference : null,
        tendered_amount: tendered,
        discount_total: discountTotal,
        discount_type: discountType,
        discount_customer_name: discountCustomerName,
        discount_customer_id: discountCustomerId,
        discount_remarks: discountRemarks,
      });
      
      setLastSale(response.data);
      setIsOfflineSale(false);
      clearCart();
      setShowCheckout(false);
      setShowReceipt(true);
      setTenderedAmount('');
      setPaymentReference('');
      setDiscountType('regular');
      setDiscountCustomerName('');
      setDiscountCustomerId('');
      setDiscountRemarks('');
      toast.success('Sale completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sale failed');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pos-grid" data-testid="pos-screen">
      {/* Left Panel - Products */}
      <div className="flex flex-col bg-background border-r border-border">
        {/* Search Bar */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search products or scan barcode..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 pl-11"
              data-testid="product-search"
            />
          </div>
        </div>
        
        {/* Categories */}
        <div className="p-4 border-b border-border">
          <ScrollArea className="w-full">
            <div className="flex gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`category-tab ${selectedCategory === cat.id ? 'active' : ''}`}
                  style={{ 
                    backgroundColor: selectedCategory === cat.id ? cat.color : undefined,
                    borderColor: selectedCategory !== cat.id ? cat.color : undefined,
                    borderWidth: selectedCategory !== cat.id ? '2px' : undefined
                  }}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
        
        {/* Product Grid */}
        <ScrollArea className="flex-1 p-4">
          <div className="product-grid">
            {filteredProducts.map(product => (
              <button
                key={product.id}
                onClick={() => addItem(product)}
                className="product-card text-left"
                data-testid={`product-${product.id}`}
              >
                <div className="font-medium text-sm line-clamp-2 mb-2">{product.name}</div>
                <div className="price-display text-primary">{formatCurrency(product.price)}</div>
                {product.sku && (
                  <div className="text-xs text-muted-foreground mt-1">{product.sku}</div>
                )}
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>
      
      {/* Right Panel - Cart */}
      <div className="flex flex-col bg-card">
        {/* Cart Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            <span className="font-semibold">Cart</span>
            <Badge variant="secondary">{itemCount}</Badge>
          </div>
          {items.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearCart}
              data-testid="clear-cart"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
        
        {/* Cart Items */}
        <ScrollArea className="flex-1">
          {items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Cart is empty</p>
              <p className="text-sm mt-1">Add products to get started</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {items.map(item => (
                <div 
                  key={item.product_id} 
                  className="cart-item p-3 rounded-lg border border-border"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-2">
                      <div className="font-medium text-sm">{item.product_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(item.unit_price)} × {item.quantity}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => removeItem(item.product_id)}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => decrementQuantity(item.product_id)}
                        data-testid={`decrement-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-10 text-center font-semibold">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => incrementQuantity(item.product_id)}
                        data-testid={`increment-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="price-display">
                      {formatCurrency(item.unit_price * item.quantity - item.line_discount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Cart Footer */}
        <div className="p-4 border-t border-border bg-muted/30 space-y-3">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {discountTotal > 0 && (
              <div className="flex justify-between text-destructive">
                <span>Discount</span>
                <span>-{formatCurrency(discountTotal)}</span>
              </div>
            )}
            {organization?.vat_mode === 'vat_registered' && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">VAT ({vatRate}%)</span>
                <span>{formatCurrency(vatAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
              <span>Total</span>
              <span className="text-primary">{formatCurrency(grandTotal)}</span>
            </div>
          </div>
          
          <Button 
            className="w-full h-14 text-lg font-semibold"
            disabled={items.length === 0}
            onClick={() => setShowCheckout(true)}
            data-testid="checkout-btn"
          >
            <Receipt className="w-5 h-5 mr-2" />
            Checkout
          </Button>
        </div>
      </div>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Complete Payment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Total Display */}
            <div className="text-center p-6 rounded-xl bg-primary/10">
              <div className="text-sm text-muted-foreground mb-1">Amount Due</div>
              <div className="text-4xl font-bold text-primary">{formatCurrency(grandTotal)}</div>
            </div>

            {/* BIR Discount Type */}
            <div className="space-y-3 border rounded-lg p-3">
              <div className="text-sm font-medium">BIR Discount Type</div>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={discountType === 'regular' ? 'default' : 'outline'}
                  onClick={() => setDiscountType('regular')}
                  size="sm"
                >
                  Regular
                </Button>
                <Button
                  type="button"
                  variant={discountType === 'senior' ? 'default' : 'outline'}
                  onClick={() => setDiscountType('senior')}
                  size="sm"
                >
                  Senior
                </Button>
                <Button
                  type="button"
                  variant={discountType === 'pwd' ? 'default' : 'outline'}
                  onClick={() => setDiscountType('pwd')}
                  size="sm"
                >
                  PWD
                </Button>
              </div>

              {['senior', 'pwd'].includes(discountType) && (
                <div className="space-y-2 pt-2">
                  <Input
                    placeholder="Customer full name"
                    value={discountCustomerName}
                    onChange={(e) => setDiscountCustomerName(e.target.value)}
                    data-testid="discount-customer-name"
                  />
                  <Input
                    placeholder={discountType === 'senior' ? 'Senior Citizen / OSCA ID No.' : 'PWD ID No.'}
                    value={discountCustomerId}
                    onChange={(e) => setDiscountCustomerId(e.target.value)}
                    data-testid="discount-customer-id"
                  />
                  <Input
                    placeholder="Remarks / optional"
                    value={discountRemarks}
                    onChange={(e) => setDiscountRemarks(e.target.value)}
                    data-testid="discount-remarks"
                  />
                  <div className="text-xs text-muted-foreground">
                    Backend will remove VAT first, then apply the mandated 20% discount.
                  </div>
                </div>
              )}
            </div>

            
            {/* Payment Method */}
            <div className="space-y-3">
              <div className="text-sm font-medium">Payment Method</div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                  className="h-14"
                  onClick={() => setPaymentMethod('cash')}
                  data-testid="payment-cash"
                >
                  <DollarSign className="w-5 h-5 mr-2" />
                  Cash
                </Button>
                {hasFeature('FEATURE_PAYMENTS_QR_MANUAL') && (
                  <Button
                    variant={paymentMethod === 'qr_manual' ? 'default' : 'outline'}
                    className="h-14"
                    onClick={() => setPaymentMethod('qr_manual')}
                    data-testid="payment-qr"
                  >
                    <QrCode className="w-5 h-5 mr-2" />
                    QR Payment
                  </Button>
                )}
              </div>
            </div>
            
            {/* Cash Input */}
            {paymentMethod === 'cash' && (
              <div className="space-y-3">
                <div className="text-sm font-medium">Tendered Amount</div>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={tenderedAmount}
                  onChange={(e) => setTenderedAmount(e.target.value)}
                  className="h-14 text-xl text-center"
                  data-testid="tendered-amount"
                />
                {parseFloat(tenderedAmount) >= grandTotal && (
                  <div className="text-center p-3 rounded-lg bg-green-500/10 text-green-600">
                    Change: {formatCurrency(parseFloat(tenderedAmount) - grandTotal)}
                  </div>
                )}
                {/* Quick amounts */}
                <div className="grid grid-cols-4 gap-2">
                  {[50, 100, 500, 1000].map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      onClick={() => setTenderedAmount(amount.toString())}
                    >
                      {amount}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {/* QR Reference */}
            {paymentMethod === 'qr_manual' && (
              <div className="space-y-3">
                <div className="text-sm font-medium">Payment Reference</div>
                <Input
                  placeholder="Enter reference number"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className="h-14"
                  data-testid="payment-reference"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCheckout} 
              disabled={processing}
              className="min-w-[120px]"
              data-testid="confirm-payment"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Sale Complete
              {isOfflineSale && (
                <Badge variant="secondary" className="gap-1">
                  <WifiOff className="w-3 h-3" />
                  Offline
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {lastSale && (
            <div className="receipt thermal-receipt p-4 bg-white text-black rounded-lg relative overflow-hidden">
              {receiptWatermark && (
                <div className={`receipt-watermark receipt-watermark-${receiptWatermark.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {receiptWatermark}
                </div>
              )}
              {/* Offline Sale Header */}
              {isOfflineSale && (
                <div className="text-center mb-2 py-2 bg-yellow-100 text-yellow-800 rounded font-semibold text-sm">
                  *** OFFLINE SALE ***
                  <div className="text-xs font-normal">Will sync when connected</div>
                </div>
              )}
              
              <div className="text-center mb-4">
                <div className="font-bold text-lg">
                  {organization?.registered_business_name || organization?.name}
                </div>
                <div className="text-sm">
                  {organization?.registered_business_address || organization?.address}
                </div>
                {organization?.tin && (
                  <div className="text-sm">
                    {organization?.vat_mode === 'vat_registered' ? 'VAT REG TIN' : 'NON VAT REG TIN'}: {organization.tin}
                    {organization?.bir_branch_code ? `-${organization.bir_branch_code}` : ''}
                  </div>
                )}
                {organization?.bir_machine_id_number && (
                  <div className="text-xs">MIN: {organization.bir_machine_id_number}</div>
                )}
                {organization?.bir_serial_number && (
                  <div className="text-xs">SN: {organization.bir_serial_number}</div>
                )}
                {organization?.bir_ptu_number && (
                  <div className="text-xs">PTU No.: {organization.bir_ptu_number}</div>
                )}
                {organization?.bir_accreditation_number && (
                  <div className="text-xs">Accreditation No.: {organization.bir_accreditation_number}</div>
                )}
                <div className="text-sm mt-2">
                  Branch: {currentBranch?.name} {currentBranch?.code ? `(${currentBranch.code})` : ''}
                </div>
              </div>
              
              <div className="border-t border-b border-dashed border-gray-400 py-2 my-2 text-sm">
                <div>Receipt No.: {lastSale.receipt_number}</div>
                {lastSale?.status && lastSale.status !== 'paid' && (
                  <div className="font-bold uppercase">Status: {lastSale.status}</div>
                )}
                {lastSale?.reprint?.count && (
                  <div className="font-bold uppercase">Reprint Copy No.: {lastSale.reprint.count}</div>
                )}
                {lastSale.reset_counter !== undefined && lastSale.reset_counter !== null && (
                  <div>Reset Counter: {String(lastSale.reset_counter).padStart(3, '0')}</div>
                )}
                <div>Date: {new Date(lastSale.created_at).toLocaleString()}</div>
                <div>Cashier: {lastSale.cashier_name || user?.name}</div>
                {['senior', 'pwd'].includes(lastSale.discount_type) && (
                  <>
                    <div>Discount Type: {lastSale.discount_type === 'senior' ? 'Senior Citizen' : 'PWD'}</div>
                    <div>SC/PWD Customer: {lastSale.discount_customer_name}</div>
                    <div>ID No.: {lastSale.discount_customer_id}</div>
                  </>
                )}
              </div>
              
              <div className="space-y-2 my-4 text-sm">
                {lastSale.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <div>
                      <div>{item.product_name}</div>
                      <div className="text-gray-600">
                        {item.quantity} × {formatCurrency(item.unit_price)}
                      </div>
                    </div>
                    <div>{formatCurrency(item.line_total)}</div>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-dashed border-gray-400 pt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(lastSale.subtotal)}</span>
                </div>
                {lastSale.regular_discount > 0 && (
                  <div className="flex justify-between">
                    <span>Regular Discount:</span>
                    <span>-{formatCurrency(lastSale.regular_discount)}</span>
                  </div>
                )}
                {lastSale.less_vat > 0 && (
                  <div className="flex justify-between">
                    <span>Less VAT:</span>
                    <span>-{formatCurrency(lastSale.less_vat)}</span>
                  </div>
                )}
                {lastSale.special_discount > 0 && (
                  <div className="flex justify-between">
                    <span>SC/PWD 20% Discount:</span>
                    <span>-{formatCurrency(lastSale.special_discount)}</span>
                  </div>
                )}
                {lastSale.discount_total > 0 && !lastSale.special_discount && (
                  <div className="flex justify-between">
                    <span>Discount:</span>
                    <span>-{formatCurrency(lastSale.discount_total)}</span>
                  </div>
                )}
                {organization?.vat_mode === 'vat_registered' && (
                  <>
                    <div className="flex justify-between">
                      <span>VATable Sales:</span>
                      <span>{formatCurrency(Math.max((lastSale.grand_total || 0) - (lastSale.vat_amount || 0), 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>VAT Amount ({vatRate}%):</span>
                      <span>{formatCurrency(lastSale.vat_amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>VAT-Exempt Sales:</span>
                      <span>{formatCurrency(lastSale.vat_exempt_sales || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Zero-Rated Sales:</span>
                      <span>{formatCurrency(lastSale.zero_rated_sales || 0)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-400">
                  <span>TOTAL:</span>
                  <span>{formatCurrency(lastSale.grand_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tendered:</span>
                  <span>{formatCurrency(lastSale.tendered_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Change:</span>
                  <span>{formatCurrency(lastSale.change_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Payment:</span>
                  <span className="uppercase">{lastSale.payment_method.replace('_', ' ')}</span>
                </div>
              </div>
              
              <div className="text-center mt-4 text-xs text-gray-700 border-t border-dashed border-gray-400 pt-2">
                {isOfflineReceipt ? (
                  <div className="font-semibold text-red-700">
                    OFFLINE TRANSACTION - NOT AN OFFICIAL RECEIPT UNTIL SYNCED
                  </div>
                ) : (
                  <div className="font-semibold">THIS SERVES AS YOUR OFFICIAL RECEIPT</div>
                )}
                {lastSale.grand_total_accumulator !== undefined && lastSale.grand_total_accumulator !== null && (
                  <div>Grand Total Accumulator: {formatCurrency(lastSale.grand_total_accumulator)}</div>
                )}
                {organization?.bir_effective_date && (
                  <div>PTU Effective Date: {new Date(organization.bir_effective_date).toLocaleDateString()}</div>
                )}
                {organization?.receipt_footer && (
                  <div className="mt-2 text-sm text-gray-600">{organization.receipt_footer}</div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="no-print">
            <Button variant="outline" onClick={() => setShowReceipt(false)}>
              Close
            </Button>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSScreen;
